import type { QueryClient } from '@tanstack/react-query';

import type { NotificationType } from '@domain/models/notification';

/**
 * Minimal shape needed to decide which caches to invalidate.
 * Accepts both the domain `Notification` and the lighter client-side
 * `Notification` interface used by `useNotifications`.
 */
export interface NotificationCacheInput {
  type: string;
  metadata?: Record<string, unknown> | null;
}

/**
 * Pure dispatch table mapping a notification type (and its metadata) to the
 * React Query cache invalidations that should follow.
 *
 * Kept in a single place so that:
 *  - It's easy to discover which views are affected by which notification.
 *  - Adding a new notification type requires touching exactly one file.
 *  - The behaviour is trivially unit-testable without React.
 *
 * Query keys mirror the conventions used across `packages/website/src/hooks`
 * (e.g. `['bookings']`, `['booking', id]`, `['activity', id]`).
 */
export function invalidateCachesForNotification(queryClient: QueryClient, notification: NotificationCacheInput): void {
  const type = notification.type as NotificationType;
  const metadata = notification.metadata ?? {};

  const bookingId = readString(metadata.bookingId);
  const activityId = readString(metadata.activityId);
  const slotId = readString(metadata.slotId);

  switch (type) {
    case 'BOOKING_BOOKED_MENTOR':
    case 'BOOKING_BOOKED_LEARNER':
    case 'BOOKING_CANCELLED_MENTOR':
    case 'BOOKING_CANCELLED_LEARNER':
    case 'BOOKING_UPDATED_MENTOR':
    case 'BOOKING_UPDATED_LEARNER': {
      invalidateBookingViews(queryClient, { bookingId });
      invalidateActivityAvailability(queryClient, { activityId, slotId });
      invalidateProfessionalDashboard(queryClient);
      break;
    }

    // Reminders are pure UX nudges — no underlying data has changed, so we
    // intentionally skip cache invalidation to avoid unnecessary refetches.
    case 'BOOKING_REMINDER_MENTOR':
    case 'BOOKING_REMINDER_LEARNER': {
      break;
    }

    // Meeting link delivered — refresh the booking so its Meeting section flips
    // from Pending to Ready (the Join action appears).
    case 'BOOKING_MEETING_LINK_MENTOR':
    case 'BOOKING_MEETING_LINK_LEARNER': {
      if (bookingId) queryClient.invalidateQueries({ queryKey: ['booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      break;
    }

    case 'ACTIVITY_VALIDATED': {
      invalidateActivityCatalog(queryClient, { activityId });
      // Newly approved activity becomes visible in public surfaces.
      queryClient.invalidateQueries({ queryKey: ['activitiesFeatured'] });
      break;
    }

    case 'ACTIVITY_DECLINED': {
      invalidateActivityCatalog(queryClient, { activityId });
      break;
    }

    case 'PROFILE_VALIDATED':
    case 'PROFILE_DECLINED': {
      invalidateUserAndProfessionalProfiles(queryClient);
      // A validated profile may surface new activities in public lists.
      queryClient.invalidateQueries({ queryKey: ['activitiesFeatured'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      break;
    }

    case 'PROFESSIONAL_BLOCKED':
    case 'PROFESSIONAL_UNBLOCKED': {
      invalidateUserAndProfessionalProfiles(queryClient);
      // Blocked professionals' activities are hidden from public listings.
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['activitiesFeatured'] });
      break;
    }

    case 'USER_BLOCKED':
    case 'USER_UNBLOCKED': {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['user-by-id'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-admin'] });
      break;
    }

    case 'DISPUTE_OPENED':
    case 'DISPUTE_ACCEPTED':
    case 'DISPUTE_REJECTED': {
      queryClient.invalidateQueries({ queryKey: ['professional-disputes'] });
      queryClient.invalidateQueries({ queryKey: ['paymentsDashboard'] });
      invalidateBookingViews(queryClient, { bookingId });
      break;
    }

    case 'PAYOUT_COMPLETED':
    case 'PAYOUT_FAILED': {
      queryClient.invalidateQueries({ queryKey: ['paymentsDashboard'] });
      invalidateBookingViews(queryClient, { bookingId });
      invalidateProfessionalDashboard(queryClient);
      // Failed payouts often correlate with Stripe account state changes.
      if (type === 'PAYOUT_FAILED') {
        queryClient.invalidateQueries({ queryKey: ['stripeAccountStatus'] });
      }
      break;
    }

    case 'REFUND_PROCESSED':
    case 'REFUND_FAILED': {
      queryClient.invalidateQueries({ queryKey: ['paymentsDashboard'] });
      invalidateBookingViews(queryClient, { bookingId });
      break;
    }

    case 'STRIPE_ONBOARDING_INCOMPLETE': {
      queryClient.invalidateQueries({ queryKey: ['stripeAccountStatus'] });
      break;
    }

    // A new review has been received but it's still pending the mentor's
    // verification. Public ratings/activity pages won't change yet — only
    // the mentor's reviews-to-verify list does.
    case 'REVIEW_RECEIVED_MENTOR': {
      if (activityId) queryClient.invalidateQueries({ queryKey: ['reviews', activityId] });
      break;
    }

    // Admin operational notifications: refresh whichever admin list now
    // has new content. Each list is keyed by status; the sidebar count
    // hooks (useProfessionalsAdminPaginated, useActivitiesAdmin) read
    // from these query keys.
    case 'ADMIN_PROFESSIONAL_PENDING_REVIEW':
    case 'ADMIN_PROFESSIONAL_REVISION_PENDING': {
      queryClient.invalidateQueries({ queryKey: ['professionals-admin'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-admin'] });
      break;
    }
    case 'ADMIN_ACTIVITY_PENDING_REVIEW':
    case 'ADMIN_ACTIVITY_REVISION_PENDING': {
      queryClient.invalidateQueries({ queryKey: ['activities-admin'] });
      break;
    }
    case 'ADMIN_REFUND_FAILED':
    case 'ADMIN_PAYOUT_FAILED': {
      // Manual-intervention admin alerts — invalidate booking + admin dashboards
      // so the new attention item shows up immediately.
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['adminBookings'] });
      break;
    }

    default: {
      // Exhaustiveness guard: if a new NotificationType is added we want a
      // compile-time nudge here so the dispatch table stays complete.
      const _exhaustive: never = type;
      void _exhaustive;
      break;
    }
  }
}

/**
 * Invalidates every list/detail surface that displays bookings.
 * Used by booking lifecycle, dispute, payout, and refund notifications.
 */
function invalidateBookingViews(queryClient: QueryClient, params: { bookingId?: string }): void {
  queryClient.invalidateQueries({ queryKey: ['bookings'] });
  queryClient.invalidateQueries({ queryKey: ['professional-bookings'] });
  queryClient.invalidateQueries({ queryKey: ['activity-bookings'] });
  if (params.bookingId) queryClient.invalidateQueries({ queryKey: ['booking', params.bookingId] });
}

/**
 * Invalidates the slot caches that drive the booking calendar and
 * activity detail pages. A new or cancelled booking changes both the slot
 * count and the slot occupancy, so these views need to refresh together.
 */
function invalidateActivityAvailability(
  queryClient: QueryClient,
  params: { activityId?: string; slotId?: string }
): void {
  queryClient.invalidateQueries({ queryKey: ['available-slots'] });
  queryClient.invalidateQueries({ queryKey: ['available-slots-all'] });
  if (params.activityId) {
    queryClient.invalidateQueries({ queryKey: ['activity', params.activityId] });
    queryClient.invalidateQueries({ queryKey: ['activitySlots', params.activityId] });
  }
  if (params.slotId) {
    queryClient.invalidateQueries({ queryKey: ['slot', params.slotId] });
  }
}

/**
 * Invalidates every list/detail surface that displays activities. Used by
 * activity validation/decline notifications and by professional-status
 * changes that affect activity visibility.
 */
function invalidateActivityCatalog(queryClient: QueryClient, params: { activityId?: string }): void {
  if (params.activityId) {
    queryClient.invalidateQueries({ queryKey: ['activity', params.activityId] });
    queryClient.invalidateQueries({ queryKey: ['activity-revisions', params.activityId] });
    queryClient.invalidateQueries({ queryKey: ['activitySlots', params.activityId] });
  }
  queryClient.invalidateQueries({ queryKey: ['activities'] });
  queryClient.invalidateQueries({ queryKey: ['user-activities'] });
  queryClient.invalidateQueries({ queryKey: ['professional-activities'] });
  queryClient.invalidateQueries({ queryKey: ['activities-admin'] });
}

/**
 * Invalidates the dashboard surfaces that display booking counts and
 * earnings — kept consistent with `useDashboardData`.
 */
function invalidateProfessionalDashboard(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
}

/**
 * Invalidates every surface that displays a user/professional's identity
 * and approval state — used by profile validation and blocking events.
 */
function invalidateUserAndProfessionalProfiles(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['user'] });
  queryClient.invalidateQueries({ queryKey: ['user-by-id'] });
  queryClient.invalidateQueries({ queryKey: ['professionals'] });
  queryClient.invalidateQueries({ queryKey: ['professionals-admin'] });
  queryClient.invalidateQueries({ queryKey: ['accounts-admin'] });
  queryClient.invalidateQueries({ queryKey: ['professional-admin-profile'] });
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}
